import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
// Types
import { IJobReassignment, IDropdownResponse } from '../../../models';
import { formatDate } from '../../../helpers';

interface IMobileReassignment {
  original: IJobReassignment;
  handleOnChange?: (
    value: number | string,
    type: 'newProjectManager' | 'newSupervisor',
    id: number
  ) => void;
  isLoadingProjectManagers: boolean;
  isLoadingSupervisors: boolean;
  projectManagers: IDropdownResponse[] | [];
  supervisors: IDropdownResponse[] | [];
  updatedJobReassignments: IJobReassignment[];
}

export const MobileStaffReassignment: FC<IMobileReassignment> = ({
  original,
  handleOnChange,
  isLoadingProjectManagers,
  isLoadingSupervisors,
  projectManagers,
  supervisors,
  updatedJobReassignments
}) => {
  const classes = useStyles();
  const {
    jobId,
    neighborhoodDescription,
    lotNumber,
    status,
    frameDate,
    projectManagerId,
    projectManagerName,
    supervisorId,
    supervisorName
  } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Box display="flex" flexWrap="wrap" flexDirection="row">
            <Typography className={classes.boldName} variant="body2">
              {neighborhoodDescription}
            </Typography>
            <Divider orientation="vertical" flexItem className={classes.divider} />
            <Typography className={classes.activeName} variant="body2">
              Lot {lotNumber}
            </Typography>
          </Box>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography component="span" variant="body2">
            {status || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Start Date:
          </FormLabel>
          <Typography component="span" variant="body2">
            {frameDate ? formatDate(frameDate) : '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Project Manager:
          </FormLabel>
          <Typography component="span" variant="body2">
            {projectManagerName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Supervisor:
          </FormLabel>
          <Typography component="span" variant="body2">
            {supervisorName || '--'}
          </Typography>
        </div>
        <Divider sx={{ margin: theme => theme.spacing(0.5, 0, 0.25) }} />
        <div className={classes.inputSummaryWrapper}>
          <FormLabel
            id={`new-pm-${jobId}-label`}
            component="span"
            className={clsx(classes.boldName, classes.subLabel)}
          >
            New Project Manager:
          </FormLabel>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              displayEmpty={!isLoadingProjectManagers}
              disabled={isLoadingProjectManagers}
              name={`new-pm-${jobId}`}
              labelId={`new-pm-${jobId}-label`}
              id={`new-pm-${jobId}`}
              value={
                updatedJobReassignments.find(item => item.jobId === jobId)?.newProjectManager ?? ''
              }
              size="small"
              onChange={({ target: { value } }) => {
                handleOnChange(value, 'newProjectManager', jobId);
              }}
              sx={{ backgroundColor: theme => theme.palette.common.white }}
            >
              <MenuItem value="">Leave As Is</MenuItem>
              {projectManagers &&
                projectManagers.map((manager, index) => {
                  if (Number(manager.value) !== projectManagerId) {
                    return (
                      <MenuItem key={`${manager.value}-${index}`} value={manager.value}>
                        {manager.description}
                      </MenuItem>
                    );
                  } else return null;
                })}
            </Select>
          </FormControl>
        </div>
        <div className={classes.inputSummaryWrapper}>
          <FormLabel
            id={`new-super-${jobId}-label`}
            component="span"
            className={clsx(classes.boldName, classes.subLabel)}
          >
            New Supervisor:
          </FormLabel>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              displayEmpty={!isLoadingSupervisors}
              disabled={isLoadingSupervisors}
              name={`new-super-${jobId}`}
              labelId={`new-super-${jobId}-label`}
              id={`new-super-${jobId}`}
              value={
                updatedJobReassignments.find(item => item.jobId === jobId)?.newSupervisor ?? ''
              }
              size="small"
              onChange={({ target: { value } }) => {
                handleOnChange(value, 'newSupervisor', jobId);
              }}
              sx={{ backgroundColor: theme => theme.palette.common.white }}
            >
              <MenuItem value="">Leave As Is</MenuItem>
              {supervisors &&
                supervisors.map((supervisor, index) => {
                  if (Number(supervisor.value) !== supervisorId) {
                    return (
                      <MenuItem key={`${supervisor.value}-${index}`} value={supervisor.value}>
                        {supervisor.description}
                      </MenuItem>
                    );
                  } else return null;
                })}
            </Select>
          </FormControl>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  inputSummaryWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  editButton: {
    padding: `2px 5px`
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  activeName: {
    color: theme.palette.grey[500]
  },
  divider: {
    margin: theme.spacing(0, 0.5)
  }
}));
